import { CurrencyPipe, DatePipe, DecimalPipe, PercentPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import {
  AIONZ_DIALOG_DATA,
  AionzDialogModule,
  AionzDialogRef,
  HeaderBottomSheetComponent
} from '@aionzdev/design-system';
import { catchError, map, of, startWith } from 'rxjs';

import {
  ProductPurchaseEntry,
  ProductPurchaseHistoryPage,
  PurchaseHistoryDialogData
} from '../../../interfaces/product-purchase-history';
import { IpcaService } from '../../../services/ipca.service';
import { ProductPurchaseHistoryService } from '../../../services/product-purchase-history.service';

/** Quantas compras o modal busca: o gráfico usa todas; a tabela pagina aqui mesmo. */
const LIMITE_COMPRAS = 100;
const POR_PAGINA = 10;

/** Área do gráfico (unidades do viewBox do SVG). */
const GRAFICO = { largura: 640, altura: 200, margemX: 12, margemY: 18 };

type Estado =
  | { tipo: 'carregando' }
  | { tipo: 'erro' }
  | { tipo: 'ok'; pagina: ProductPurchaseHistoryPage };

interface PontoGrafico {
  x: number;
  y: number;
  entry: ProductPurchaseEntry;
}

function paginadorEmPortugues(): MatPaginatorIntl {
  const intl = new MatPaginatorIntl();
  intl.itemsPerPageLabel = 'Compras por página:';
  intl.nextPageLabel = 'Próxima página';
  intl.previousPageLabel = 'Página anterior';
  intl.getRangeLabel = (page, pageSize, length) => {
    if (!length) return `0 de ${length}`;
    const inicio = page * pageSize;
    return `${inicio + 1} – ${Math.min(inicio + pageSize, length)} de ${length}`;
  };
  return intl;
}

/**
 * Histórico completo de compras do produto (pedido da Conceito): resumo de preços, último
 * preço corrigido pelo IPCA, gráfico da variação e a lista de compras.
 */
@Component({
  selector: 'aionz-purchase-history-dialog',
  imports: [
    AionzDialogModule,
    HeaderBottomSheetComponent,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatTableModule,
    CurrencyPipe,
    DatePipe,
    DecimalPipe,
    PercentPipe
  ],
  providers: [{ provide: MatPaginatorIntl, useFactory: paginadorEmPortugues }],
  templateUrl: './purchase-history-dialog.component.html',
  styleUrl: './purchase-history-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PurchaseHistoryDialogComponent {
  protected readonly data = inject<PurchaseHistoryDialogData>(AIONZ_DIALOG_DATA);
  private readonly dialogRef = inject(AionzDialogRef);

  protected readonly grafico = GRAFICO;
  protected readonly porPagina = POR_PAGINA;
  protected readonly colunas = ['date', 'supplier', 'quantity', 'unitPrice', 'vsAverage', 'origin'];

  protected readonly estado = toSignal(
    inject(ProductPurchaseHistoryService)
      .load(this.data, 1, LIMITE_COMPRAS)
      .pipe(
        map(pagina => ({ tipo: 'ok', pagina }) as Estado),
        catchError(() => of({ tipo: 'erro' } as Estado)),
        startWith({ tipo: 'carregando' } as Estado)
      ),
    { initialValue: { tipo: 'carregando' } as Estado }
  );

  protected readonly pagina = computed(() => {
    const estado = this.estado();
    return estado.tipo === 'ok' ? estado.pagina : null;
  });

  /** IPCA desde a última compra, para o "preço balizador". */
  private readonly ipca = toSignal(
    this.data.lastPurchaseDate && this.data.lastPurchasePrice
      ? inject(IpcaService).acumuladoDesde(new Date(this.data.lastPurchaseDate))
      : of(null),
    { initialValue: null }
  );

  protected readonly ultimoCorrigido = computed(() => {
    const ipca = this.ipca();
    const ultimo = this.data.lastPurchasePrice;
    if (!ipca || ipca.pendente || !ultimo) return null;
    return { valor: ultimo * ipca.fator, variacao: ipca.fator - 1 };
  });

  /** Menor preço cotado agora contra a média do histórico. */
  protected readonly cotadoVsMedia = computed(() => {
    const media = this.pagina()?.summary.averagePrice;
    const atual = this.data.currentLowestPrice;
    if (!media || !atual) return null;
    return { valor: atual, diferenca: (atual - media) / media };
  });

  // ---- tabela ----

  protected readonly indicePagina = signal(0);
  protected readonly linhas = computed(() => {
    const inicio = this.indicePagina() * POR_PAGINA;
    return (this.pagina()?.entries ?? []).slice(inicio, inicio + POR_PAGINA);
  });

  protected mudarPagina(evento: PageEvent): void {
    this.indicePagina.set(evento.pageIndex);
  }

  protected diferencaDaMedia(preco: number): number | null {
    const media = this.pagina()?.summary.averagePrice;
    return media ? (preco - media) / media : null;
  }

  // ---- gráfico ----

  /** Pontos do mais antigo para o mais recente, na escala do viewBox. */
  protected readonly pontos = computed<PontoGrafico[]>(() => {
    const entries = [...(this.pagina()?.entries ?? [])].reverse();
    if (entries.length < 2) return [];
    const precos = entries.map(entry => entry.unitPrice);
    const { min, max } = this.faixa(precos);
    const larguraUtil = GRAFICO.largura - GRAFICO.margemX * 2;
    return entries.map((entry, index) => ({
      x: GRAFICO.margemX + (larguraUtil * index) / (entries.length - 1),
      y: this.escalaY(entry.unitPrice, min, max),
      entry
    }));
  });

  protected readonly linha = computed(() =>
    this.pontos()
      .map((ponto, index) => `${index ? 'L' : 'M'}${ponto.x.toFixed(1)},${ponto.y.toFixed(1)}`)
      .join(' ')
  );

  protected readonly yMedia = computed(() => {
    const media = this.pagina()?.summary.averagePrice;
    const entries = this.pagina()?.entries ?? [];
    if (!media || entries.length < 2) return null;
    const { min, max } = this.faixa(entries.map(entry => entry.unitPrice));
    return this.escalaY(media, min, max);
  });

  protected readonly pontoAtivo = signal<PontoGrafico | null>(null);

  private faixa(precos: number[]): { min: number; max: number } {
    const min = Math.min(...precos);
    const max = Math.max(...precos);
    // folga para a linha não encostar nas bordas; preço constante vira uma faixa de ±5%
    const folga = max === min ? max * 0.05 || 1 : (max - min) * 0.1;
    return { min: min - folga, max: max + folga };
  }

  private escalaY(preco: number, min: number, max: number): number {
    const alturaUtil = GRAFICO.altura - GRAFICO.margemY * 2;
    return GRAFICO.margemY + alturaUtil * (1 - (preco - min) / (max - min));
  }

  protected fechar(): void {
    this.dialogRef.close();
  }
}
